import React from 'react';
import { Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { Company, Establishment, EmissionPoint } from './types';

interface HeaderSectionProps {
    selectedCompany: Company | null;
    selectedEstablishment: Establishment | null;
    selectedEmissionPoint: EmissionPoint | null;
    jwtPayload?: any;
    onCompanyChange?: (company: Company) => void;
    onEstablishmentChange?: (establishment: Establishment) => void;
    onEmissionPointChange?: (emissionPoint: EmissionPoint) => void;
    companies?: Company[];
    establishments?: Establishment[];
    emissionPoints?: EmissionPoint[];
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
    selectedCompany,
    selectedEstablishment,
    selectedEmissionPoint,
    jwtPayload,
    onCompanyChange,
    onEstablishmentChange,
    onEmissionPointChange,
    companies = [],
    establishments = [],
}) => {
    const isFromToken = jwtPayload && (
        selectedCompany?.id === jwtPayload.companyId || 
        selectedCompany?._id === jwtPayload.companyId
    );

    return (
        <Card className="shadow border-0 h-100 overflow-hidden rounded-3">
            <div className="bg-soft-primary p-3 border-bottom border-light">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                        <i className="ri-store-2-fill me-2 fs-18"></i>
                        Información del Emisor
                    </h5>
                    {isFromToken && (
                        <span className="badge bg-white text-success shadow-sm border border-success border-opacity-25">
                            <i className="ri-shield-check-fill me-1"></i>
                            Autenticado
                        </span>
                    )}
                </div>
            </div>
            <CardBody className="p-4 bg-white">
                <Row className="g-4">
                    <Col md={4}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Empresa <span className="text-danger">*</span>
                            </Label>
                            {companies.length > 0 ? (
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="ri-building-line text-muted"></i>
                                    </span>
                                    <Input
                                        type="select"
                                        className={`form-select border-start-0 ps-0 ${isFromToken ? "bg-soft-success border-success text-success fw-medium" : ""}`}
                                        value={selectedCompany?.id || selectedCompany?._id || ''}
                                        onChange={(e) => {
                                            if (onCompanyChange) {
                                                const company = companies.find(c => c.id === e.target.value || c._id === e.target.value);
                                                if (company) onCompanyChange(company);
                                            }
                                        }}
                                        disabled={!onCompanyChange}
                                    >
                                        {companies.map(company => (
                                            <option key={company.id} value={company.id || company._id}>
                                                {company.businessName}
                                            </option>
                                        ))}
                                    </Input>
                                </div>
                            ) : (
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="ri-building-line text-muted"></i>
                                    </span>
                                    <Input 
                                        value={selectedCompany?.businessName || ''} 
                                        disabled 
                                        className="bg-light border-start-0 ps-0" 
                                    />
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Establecimiento <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-store-line text-muted"></i>
                                </span>
                                {establishments.length > 0 ? (
                                    <Input
                                        type="select"
                                        className="form-select border-start-0 ps-1"
                                        value={selectedEstablishment?.id || selectedEstablishment?._id || ''}
                                        onChange={(e) => {
                                            if (onEstablishmentChange) {
                                                const establishment = establishments.find(est => est.id === e.target.value || est._id === e.target.value);
                                                if (establishment) onEstablishmentChange(establishment);
                                            }
                                        }}
                                        disabled={!onEstablishmentChange}
                                    >
                                        {establishments.map(establishment => (
                                            <option key={establishment.id} value={establishment.id || establishment._id}>
                                                {establishment.code} - {establishment.address || 'Sin dirección'}
                                            </option>
                                        ))}
                                    </Input>
                                ) : (
                                    <Input 
                                        value={selectedEstablishment?.code}
                                        disabled
                                        className="bg-light border-start-0 ps-0"
                                    />
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Punto de emisión <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-printer-line text-muted"></i>
                                </span>
                                {selectedEstablishment?.emissionPoints && selectedEstablishment.emissionPoints.length > 0 ? (
                                    <Input
                                        type="select"
                                        className="form-select border-start-0 ps-1"
                                        value={selectedEmissionPoint?.id || selectedEmissionPoint?._id || ''}
                                        onChange={(e) => {
                                            if (onEmissionPointChange) {
                                                const point = selectedEstablishment.emissionPoints?.find((p: any) => p.id === e.target.value || p._id === e.target.value);
                                                if (point) onEmissionPointChange(point);
                                            }
                                        }}
                                        disabled={!onEmissionPointChange}
                                    >
                                        {selectedEstablishment.emissionPoints.map((point: any) => (
                                            <option key={point.id} value={point.id || point._id}>
                                                {point.code}
                                            </option>
                                        ))}
                                    </Input>
                                ) : (
                                    <Input 
                                        value={selectedEmissionPoint?.code || '001'} 
                                        disabled 
                                        className="bg-light border-start-0 ps-0" 
                                    />
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default HeaderSection;
